import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import { NameValue } from '../../domain/metrics';

export const PALETTE = ['#005EB8', '#00B398', '#41B6E6', '#FFB81C', '#25282A', '#D7D2CB', '#EF3340', '#FF7F32', '#9B26B6', '#BC204B'];

export const tooltipStyle = {
  background: 'var(--surface)',
  color: 'var(--text)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  boxShadow: 'var(--shadow-md)',
  fontSize: 12,
};
export const axisTick = { fontSize: 11, fill: 'var(--text-tertiary)' };
export const GRID = 'var(--chart-grid)';
export const CHART_CURSOR = 'var(--chart-cursor)';
export type MetricKey = 'tareas' | 'horas';

/** Ícono de ayuda con tooltip. Pensado para usuarios no técnicos. */
export function InfoTip({ text }: { text: string }) {
  return (
    <span className="infotip" tabIndex={0} role="img" aria-label={`Ayuda: ${text}`}>
      i
      <span className="infotip-bubble" role="tooltip">{text}</span>
    </span>
  );
}

/** Encabezado de cada tablero: qué muestra y qué preguntas responde. */
export function BoardIntro({ titulo, objetivo, preguntas }: { titulo: string; objetivo: string; preguntas?: string[] }) {
  return (
    <div className="board-intro">
      <h2>{titulo}</h2>
      <p>{objetivo}</p>
      {preguntas && preguntas.length > 0 && (
        <ul className="preguntas">{preguntas.map((p, i) => <li key={i}>{p}</li>)}</ul>
      )}
    </div>
  );
}

export function Card({ title, hint, help, children, action }: { title: string; hint?: string; help?: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="panel">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h2 style={{ margin: '0 0 4px' }}>{title}{help && <InfoTip text={help} />}</h2>
        {action}
      </div>
      {hint && <div className="hint" style={{ marginTop: 0, marginBottom: 8 }}>{hint}</div>}
      {children}
    </div>
  );
}

export function Kpi({ label, value, tone, help, sub }: { label: string; value: string; tone?: 'ok' | 'warn' | 'bad'; help?: string; sub?: string }) {
  const color = tone === 'bad' ? 'var(--danger)' : tone === 'warn' ? 'var(--warn-strong)' : tone === 'ok' ? 'var(--ok-strong)' : undefined;
  return (
    <div className="panel kpi-card">
      <div className="kpi-label">{label}{help && <InfoTip text={help} />}</div>
      <div className="kpi" style={color ? { color } : undefined}>{value}</div>
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  );
}

export function MetricToggle({ value, onChange }: { value: MetricKey; onChange: (metric: MetricKey) => void }) {
  return (
    <div className="metric-toggle" role="radiogroup" aria-label="Métrica del gráfico">
      {(['tareas', 'horas'] as MetricKey[]).map((m) => (
        <button
          key={m}
          type="button"
          role="radio"
          aria-checked={value === m}
          className={value === m ? 'active' : ''}
          onClick={() => onChange(m)}
        >
          {m === 'tareas' ? 'Tareas' : 'Horas'}
        </button>
      ))}
    </div>
  );
}

export function BarCard({ title, data, hint, dataKey = 'value', color = '#4f46e5', horizontal = false, action, allowDecimals = false, height, tickFontSize = 11, yAxisWidth = 150 }: {
  title: string; data: NameValue[]; hint?: string; dataKey?: string; color?: string; horizontal?: boolean; action?: React.ReactNode; allowDecimals?: boolean; height?: number; tickFontSize?: number; yAxisWidth?: number;
}) {
  const tick = { fontSize: tickFontSize, fill: 'var(--text-tertiary)' };
  const h = height ?? Math.max(260, horizontal ? data.length * 28 + 40 : 260);
  return (
    <Card title={title} hint={hint} action={action}>
      <ResponsiveContainer width="100%" height={h}>
        {horizontal ? (
          <BarChart data={data} layout="vertical" margin={{ left: 10, right: 16 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
            <XAxis type="number" allowDecimals={allowDecimals} tick={tick} />
            <YAxis type="category" dataKey="name" tick={tick} width={yAxisWidth} interval={0} />
            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: CHART_CURSOR }} />
            <Bar dataKey={dataKey} fill={color} radius={[0, 4, 4, 0]} maxBarSize={30} />
          </BarChart>
        ) : (
          <BarChart data={data} margin={{ left: -10, bottom: 30 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
            <XAxis dataKey="name" tick={tick} angle={-25} textAnchor="end" interval={0} height={50} />
            <YAxis allowDecimals={allowDecimals} tick={tick} />
            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: CHART_CURSOR }} />
            <Bar dataKey={dataKey} fill={color} radius={[4, 4, 0, 0]} maxBarSize={48} />
          </BarChart>
        )}
      </ResponsiveContainer>
    </Card>
  );
}

export function DonutCard({ title, data, hint, colors, action, pct, height = 260 }: { title: string; data: NameValue[]; hint?: string; colors?: string[]; action?: React.ReactNode; pct?: boolean; height?: number }) {
  const label = pct
    ? (e: any) => `${e.value} · ${Math.round((e.percent || 0) * 100)}%`
    : (e: any) => e.value;
  return (
    <Card title={title} hint={hint} action={action}>
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} label={label}>
            {data.map((_, i) => <Cell key={i} fill={(colors || PALETTE)[i % (colors || PALETTE).length]} />)}
          </Pie>
          <Tooltip contentStyle={tooltipStyle} /><Legend wrapperStyle={{ fontSize: 12 }} />
        </PieChart>
      </ResponsiveContainer>
    </Card>
  );
}

export function pct(x: number, total: number): string {
  return total ? `${Math.round((x / total) * 100)}%` : '—';
}

// ---- Export CSV ----
export function downloadCsv(filename: string, rows: Record<string, unknown>[]): void {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const esc = (v: unknown) => {
    const s = v == null ? '' : String(v);
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.join(','), ...rows.map((r) => headers.map((h) => esc(r[h])).join(','))].join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function ExportButton({ filename, rows }: { filename: string; rows: Record<string, unknown>[] }) {
  return (
    <button className="ghost sm" disabled={!rows.length} onClick={() => downloadCsv(filename, rows)}>
      Exportar CSV
    </button>
  );
}
